import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { messages } from '@fruitways/shared';
import { Button, Icon, type IconName } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import type { SubmitOutcome } from '@/db/estimateActions';
import { useCapture } from '@/capture/CaptureContext';
import './capture.css';

interface SuccessState {
  outcome: SubmitOutcome;
  farmName: string;
  weekNumber: number;
  total?: number;
}

const COPY: Record<SubmitOutcome, { icon: IconName; color: string; title: string; body: string }> =
  {
    submitted: {
      icon: 'checkmark-circle',
      color: 'var(--color-success)',
      title: messages.success.submittedTitle,
      body: messages.success.submittedBody,
    },
    saved: {
      icon: 'checkmark-circle',
      color: 'var(--color-success)',
      title: messages.success.savedTitle,
      body: messages.success.savedBody,
    },
    updated: {
      icon: 'checkmark-circle',
      color: 'var(--color-success)',
      title: messages.success.updatedTitle,
      body: messages.success.updatedBody,
    },
    deleted: {
      icon: 'trash-outline',
      color: 'var(--color-danger)',
      title: messages.success.deletedTitle,
      body: messages.success.deletedBody,
    },
  };

/** Outcome screen after submit, update or delete. State comes from the navigation. */
export function SuccessScreen() {
  const nav = useNavigate();
  const state = useLocation().state as SuccessState | null;
  const { clear } = useCapture();
  // The draft is done with once this screen shows. Clearing here, not before navigating, keeps
  // the review screen's guard from redirecting while the route change is still pending.
  useEffect(() => {
    clear();
  }, [clear]);
  if (!state) return <Navigate to="/" replace />;
  const c = COPY[state.outcome];

  return (
    <AppFrame title="">
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="fw-success">
          <Icon name={c.icon} size={56} color={c.color} />
          <span className="fw-title">{c.title}</span>
          <span style={{ font: 'var(--type-body)', color: 'var(--text-muted)' }}>{c.body}</span>
          {state.total !== undefined && state.outcome !== 'deleted' && (
            <span className="fw-secondary fw-tnum">
              {messages.success.meta(state.farmName, state.weekNumber, state.total)}
            </span>
          )}
        </div>
        <div style={{ padding: '0 var(--screen-padding-x) var(--space-6)' }}>
          <Button onClick={() => nav('/', { replace: true })}>
            {messages.success.backToDashboard}
          </Button>
        </div>
      </div>
    </AppFrame>
  );
}
